<script setup lang="ts">
import { computed, useAttrs } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { objectWithoutKeys } from '@/helpers'
import DropdownMenu from '@/components/dropdown-menu.vue'
import BaseButton from './base-button.vue'
import { type BaseButtonProps, BaseButtonDefaults } from './_constants'

defineOptions({ inheritAttrs: false })

type ButtonPrimaryProps = BaseButtonProps & {
  dark?: boolean
  menuAlign?: 'left' | 'right'
  menuIcon?: IconDefinition
}

const props = withDefaults(defineProps<ButtonPrimaryProps>(), {
  ...BaseButtonDefaults,
  menuAlign: 'right'
})

const attrs = useAttrs()
const baseButtonProps = computed(() => objectWithoutKeys(props, ['dark', 'menuAlign', 'menuIcon']))
const bindToBaseButton = computed(() => ({
  ...attrs,
  ...baseButtonProps.value
}))
</script>

<template>
  <DropdownMenu v-if="$slots.menu" :align="menuAlign" :class="$style.dropdownWrap">
    <BaseButton class="btn-primary" :class="$style['button-content']" v-bind="bindToBaseButton">
      <slot />
      <span :class="$style.btnDivider" aria-hidden="true"></span>
      <span :class="$style.caret" aria-hidden="true">
        <FontAwesomeIcon v-if="menuIcon" :icon="menuIcon" />
        <template v-else>▾</template>
      </span>
    </BaseButton>

    <template #items>
      <slot name="menu" />
    </template>
  </DropdownMenu>

  <BaseButton v-else class="btn-primary" v-bind="bindToBaseButton">
    <slot />
  </BaseButton>
</template>

<style module>
.button-content {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btnDivider {
  width: 1px;
  height: 16px;
  background: rgba(255, 255, 255, 0.35);
  display: inline-block;
}

.caret {
  line-height: 1;
  display: inline-flex;
  align-items: center;
}

.caret :global(svg) {
  width: 12px;
  height: 12px;
}

</style>
